import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Mail, RefreshCw, Users, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (v) => (v == null ? 'N/A' : `${(v * 100).toFixed(1)}%`);

export const MailchimpSettings = () => {
  const [status, setStatus] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [listId, setListId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await axios.get(`${API}/mailchimp/status`);
      setStatus(s.data);
      if (s.data.connected) {
        const [a, c, m] = await Promise.all([
          axios.get(`${API}/mailchimp/audiences`),
          axios.get(`${API}/mailchimp/campaigns`),
          axios.get(`${API}/mailchimp/metrics`),
        ]);
        setAudiences(a.data);
        setCampaigns(c.data.campaigns || []);
        setMetrics(m.data);
        if (a.data.length && !listId) setListId(a.data[0].id);
      }
    } catch (e) {
      setStatus({ configured: false, connected: false, message: e.response?.status === 403 ? 'Admin access required' : 'Failed to load Mailchimp status' });
    }
  }, [listId]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const syncContacts = async () => {
    if (!listId) { toast.error('Select an audience first'); return; }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/mailchimp/sync-contacts`, { list_id: listId });
      toast.success(`Synced ${res.data.synced} contacts (${res.data.skipped_unsubscribed} unsubscribed skipped, ${res.data.failed} failed)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const fetchReport = async (id) => {
    try {
      const r = await axios.get(`${API}/mailchimp/campaigns/${id}/report`);
      toast.success(`${r.data.title || 'Campaign'}: ${r.data.emails_sent ?? 'N/A'} sent, open rate ${pct(r.data.opens?.open_rate)}`);
      const m = await axios.get(`${API}/mailchimp/metrics`);
      setMetrics(m.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report unavailable');
    }
  };

  if (!status) return null;

  return (
    <Card data-testid="mailchimp-settings">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-primary" /> Mailchimp</CardTitle>
          <CardDescription>Contact sync, campaigns and email marketing metrics</CardDescription>
        </div>
        <Badge className={status.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} data-testid="mailchimp-status-badge">
          {status.connected ? `Connected${status.account_name ? ` — ${status.account_name}` : ''}` : status.configured ? 'Key invalid' : 'Not connected'}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.connected ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="mailchimp-not-configured">
            <p>{status.message || status.error || 'Mailchimp is not connected.'}</p>
            <p className="mt-2">To connect: ask the assistant to add your <b>Mailchimp API key</b> (found in Mailchimp → Account → Extras → API keys). Once added, contact sync, campaign management and reporting activate automatically — nothing is simulated until then.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[220px]">
                <p className="text-xs text-muted-foreground">Audience</p>
                <Select value={listId} onValueChange={setListId}>
                  <SelectTrigger data-testid="mailchimp-audience-select"><SelectValue placeholder="Select audience" /></SelectTrigger>
                  <SelectContent>
                    {audiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.stats?.member_count ?? '?'} members)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" className="gap-1.5" onClick={syncContacts} disabled={syncing} data-testid="mailchimp-sync-btn">
                <Users className="h-4 w-4" /> {syncing ? 'Syncing…' : 'Sync CRM Contacts'}
              </Button>
              <Button size="sm" variant="ghost" onClick={load} className="gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
            </div>
            {status.last_sync && (
              <p className="text-xs text-muted-foreground">
                Last sync: {new Date(status.last_sync.created_at).toLocaleString('en-ZA')} — {status.last_sync.synced} synced, {status.last_sync.skipped_unsubscribed} unsubscribed skipped
              </p>
            )}
            {metrics?.available && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mailchimp-metrics">
                {[['Emails Sent', metrics.total_emails_sent], ['Avg Open Rate', pct(metrics.avg_open_rate)],
                  ['Avg Click Rate', pct(metrics.avg_click_rate)], ['Unsubscribes', metrics.total_unsubscribes]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">{l}</p>
                    <p className="text-xl font-bold">{v ?? 'N/A'}</p>
                  </div>
                ))}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-right"><BarChart3 className="h-3.5 w-3.5 inline" /></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm font-medium">{c.title || c.subject || c.id}</TableCell>
                    <TableCell className="text-sm">{c.list_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                    <TableCell className="text-sm font-mono">{c.emails_sent ?? '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fetchReport(c.id)} data-testid={`mailchimp-report-${c.id}`}>Report</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {campaigns.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No campaigns</TableCell></TableRow>}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};
